import React, { useState } from 'react';
import CalcShell from '../CalcShell';

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#4c7b25] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`} className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}

function ResultLine({ label, value }) {
  return (
    <div className="mt-3 bg-white rounded-lg p-3 border border-slate-200">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-mono text-lg font-semibold text-blue-700 break-all">{value}</div>
    </div>
  );
}

function HexArithSection() {
  const [a, setA] = useState('8AB');
  const [b, setB] = useState('B78');
  const [op, setOp] = useState('+');
  const [result, setResult] = useState(null);
  const calc = () => {
    const na = parseInt(a.trim(), 16);
    const nb = parseInt(b.trim(), 16);
    if (isNaN(na) || isNaN(nb)) { setResult({ err: 'Invalid hexadecimal input' }); return; }
    let r;
    if (op === '+') r = na + nb;
    else if (op === '−') r = na - nb;
    else if (op === '×') r = na * nb;
    else if (op === '÷') r = nb === 0 ? null : na / nb;
    if (r === null) { setResult({ err: 'Divide by zero' }); return; }
    const isInt = Number.isInteger(r);
    setResult({
      hex: (r < 0 ? '-' : '') + Math.abs(isInt ? r : Math.floor(r)).toString(16).toUpperCase() + (isInt ? '' : ` (approx from ${r.toFixed(6)})`),
      dec: r,
      bin: (r < 0 ? '-' : '') + Math.abs(isInt ? r : Math.floor(r)).toString(2),
    });
  };
  const clear = () => { setA(''); setB(''); setOp('+'); setResult(null); };
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900">Hexadecimal Calculation—Add, Subtract, Multiply, or Divide</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input value={a} onChange={e => setA(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-32" />
          <select value={op} onChange={e => setOp(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white">
            <option>+</option><option>−</option><option>×</option><option>÷</option>
          </select>
          <input value={b} onChange={e => setB(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-32" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="hex-arith" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Hexadecimal" value={result.hex} />
            <ResultLine label="Decimal" value={result.dec} />
            <ResultLine label="Binary" value={result.bin} />
          </>
        )}
      </div>
    </div>
  );
}

function HexToDecSection() {
  const [hex, setHex] = useState('DAD');
  const [result, setResult] = useState(null);
  const calc = () => {
    const n = parseInt(hex.trim(), 16);
    if (isNaN(n)) { setResult({ err: 'Invalid hex value' }); return; }
    setResult({ dec: n, bin: n.toString(2), oct: n.toString(8) });
  };
  const clear = () => { setHex(''); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Convert Hexadecimal Value to Decimal Value</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-700">Hexadecimal Value:</span>
          <input value={hex} onChange={e => setHex(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-40" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="hex-to-dec" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Decimal" value={result.dec} />
            <ResultLine label="Binary" value={result.bin} />
            <ResultLine label="Octal" value={result.oct} />
          </>
        )}
      </div>
    </div>
  );
}

function DecToHexSection() {
  const [dec, setDec] = useState(170);
  const [result, setResult] = useState(null);
  const calc = () => {
    const n = Number(dec);
    if (!Number.isFinite(n)) { setResult({ err: 'Invalid decimal value' }); return; }
    const i = Math.floor(Math.abs(n));
    const sign = n < 0 ? '-' : '';
    setResult({ hex: sign + i.toString(16).toUpperCase(), bin: sign + i.toString(2), oct: sign + i.toString(8) });
  };
  const clear = () => { setDec(0); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Convert Decimal Value to Hexadecimal Value</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-700">Decimal Value:</span>
          <input type="number" value={dec} onChange={e => setDec(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-40" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="dec-to-hex" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Hexadecimal" value={result.hex} />
            <ResultLine label="Binary" value={result.bin} />
            <ResultLine label="Octal" value={result.oct} />
          </>
        )}
      </div>
    </div>
  );
}

export default function HexCalc() {
  return (
    <CalcShell look="cnet" title="Hex Calculator" subtitle="Modify the values and click the Calculate button to use">
      <HexArithSection />
      <HexToDecSection />
      <DecToHexSection />
    </CalcShell>
  );
}
